/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { ELEMENT_PARAGRAPH } from '@udecode/plate';
import cn from 'classnames';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import dynamic from 'next/dynamic';
import * as React from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { BlockEditorProps } from 'src/components/BlockEditor';
import { useBoardStore } from 'src/features/boards';
import { EditorProvider } from 'src/features/collaborative-editor/components/EditorProvider';
import { useTaskAccess } from 'src/features/files/hooks/useTaskAccess';
import { getFormattedValue } from 'src/utils/getFormattedValue';

import { useTasksStore } from '../store/useTasksStore';
import { Task } from '../types/tasks';

const BlockEditor = dynamic<BlockEditorProps>(() =>
  import('src/components/BlockEditor').then((mod) => mod.BlockEditor)
);

const { useState, useMemo } = React;

export const EditItemDescription = ({
  task,
}: {
  task: Partial<Task> & { isLoading?: boolean };
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const onUpdateItemField = useTasksStore((s) => s.onUpdateItemField);
  const isFullScreen = useTasksStore((s) => s.isFullScreen);
  const boardsStore = useBoardStore();

  const [value, setValue] = useState<any>(getInitialValue(task?.description));
  const debouncedOnChange = useMemo(
    () =>
      debounce((string) => {
        if (task?.id) {
          onUpdateItemField({ item: { description: string, id: task.id }, keys: ['description'] });
        }
      }, 600),
    [onUpdateItemField, task?.id]
  );

  const { isReadOnly } = useTaskAccess(task?.id || '');

  if (!task) return <></>;

  return (
    <div>
      <div className="flex items-center gap-8 text-sm text-gray-500 ">
        <span className="">DESCRIPTION</span>
        <div className="flex-grow border-t" />
      </div>
      <DndProvider backend={HTML5Backend}>
        <EditorProvider id={task.id || 'task'}>
          <BlockEditor
            onChange={(e) => {
              if (
                getFormattedValue(e).trim() !== getFormattedValue(value).trim() ||
                !compareData(e, value)
              ) {
                setValue(e);
                debouncedOnChange(e);
              }
            }}
            selectionToolbarProps={{
              hide: {
                changeBgColor: !isFullScreen,
                changeBlock: !isFullScreen,
                changeColor: !isFullScreen,
                comment: true,
              },
            }}
            id={task.id}
            value={value}
            isReadOnly={isReadOnly}
            editableProps={{
              onFocus: () => setIsFocused(true),
              onBlur: () => setIsFocused(false),
              className: cn('rounded py-10 px-34'),
            }}
            addNewBlockConfig={{ style: { paddingBottom: '2.4rem' } }}
          />
        </EditorProvider>
      </DndProvider>
    </div>
  );
};

function getInitialValue(value: any[] | undefined) {
  if (value && value[0]?.type) {
    return value;
  }
  Array.isArray(value) &&
    value?.map((obj) => {
      if (!obj.type) {
        obj.type = ELEMENT_PARAGRAPH;
      }
    });
  return value && value.length > 0 ? value : intialEditorValue;
}

function compareData(editedValue, value) {
  return isEqual(editedValue, value);
}

const intialEditorValue = [{ children: [{ text: '' }], type: ELEMENT_PARAGRAPH }];
